"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { leaveService, holidayService, userService, authService } from "../services/api.service"
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card"
import AppLayout from "../layouts/AppLayout"
import { Button } from "../components/ui/button"

// Add CSS for custom scrollbar
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c5c5c5;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #c5c5c5 #f1f1f1;
  }
`

const DashboardPage = () => {
  const { user, refreshUserData } = useAuth()
  const location = useLocation()
  const [leaveRequests, setLeaveRequests] = useState([])
  const [pendingApprovals, setPendingApprovals] = useState([])
  const [upcomingHolidays, setUpcomingHolidays] = useState([])
  const [upcomingLeaves, setUpcomingLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefreshed, setLastRefreshed] = useState(new Date())
  const [directUserData, setDirectUserData] = useState(null)
  const [leaveTypes, setLeaveTypes] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Use ref to track initialization instead of state to avoid dependency issues
  const dashboardInitiatedRef = useRef(false)
  const fetchingRef = useRef(false) // Add flag to prevent multiple fetches

  // Simplified leave statistics with initial values
  const [leaveStats, setLeaveStats] = useState({
    total: 0,
    used: 0,
    remaining: user?.leave_balance || 0,
    percentage: 0,
  })

  // Simplified sick leave statistics with initial values
  const [sickLeaveStats, setSickLeaveStats] = useState({
    total: 0,
    used: 0,
    remaining: user?.sick_leave_balance || 0,
    percentage: 0,
  })

  // Fetch the latest user data directly from the API - simplified version
  const fetchLatestUserData = useCallback(async () => {
    try {
      const timestamp = new Date().getTime()
      // Use authService.getCurrentUser() instead of userService.getCurrentUser()
      const response = await authService.getCurrentUser({ _t: timestamp })
      
      if (!response.user) {
        console.error("No user data returned from API")
        return null
      }
      
      // Log the exact leave balance values
      console.log("Leave balance:", response.user.leave_balance, "Type:", typeof response.user.leave_balance)
      console.log("Sick leave balance:", response.user.sick_leave_balance, "Type:", typeof response.user.sick_leave_balance)
      
      // Store the full user data object
      setDirectUserData(response.user)
      
      // Update leave stats directly with the values from API
      setLeaveStats(prev => ({
        ...prev,
        remaining: response.user.leave_balance || prev.remaining
      }))
      
      setSickLeaveStats(prev => ({
        ...prev,
        remaining: response.user.sick_leave_balance || prev.remaining
      }))
      
      return response.user
    } catch (error) {
      console.error("Error fetching latest user data:", error)
      return null
    }
  }, [])

  // Calculate duration in days - count all days between start and end dates (inclusive)
  const calculateDuration = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return 0

    try {
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set to start of day
      start.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)

      // Calculate difference in days (inclusive)
      const diffTime = Math.abs(end - start)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1

      return diffDays
    } catch (e) {
      console.error("Error calculating duration:", e)
      return 0
    }
  }, [])

  // Modified function to ensure correct calculation of leave statistics - simplified version
// Modified function to ensure correct calculation of leave statistics - FIXED NaN issue
// Modified function to calculate leave statistics based on actual data - NO hardcoded fallbacks
const calculateLeaveStatistics = useCallback((leaveRequests, types, userData) => {
  if (!userData) {
    return { 
      regular: {total: 0, used: 0, remaining: 0, percentage: 0}, 
      sick: {total: 0, used: 0, remaining: 0, percentage: 0} 
    }
  }
  
  // Use actual values from the database
  const remainingRegularLeave = Number(userData.leave_balance) || 0      
  const remainingSickLeave = Number(userData.sick_leave_balance) || 0
  
  console.log("Calculating with actual database values:", {
    remainingRegular: remainingRegularLeave,
    remainingSick: remainingSickLeave
  })
  
  // Find sick leave type IDs
  const sickLeaveTypeIds = Array.isArray(types) 
    ? types
        .filter(type => type.name && type.name.toLowerCase().includes('sick'))
        .map(type => Number(type.id))
    : []
  
  // Filter for approved leaves that aren't adjustments
  const approvedLeaves = Array.isArray(leaveRequests) 
    ? leaveRequests.filter((leave) => leave.status === "approved" && !leave.is_adjustment)
    : []

  let usedRegularLeaveDays = 0
  let usedSickLeaveDays = 0

  // Calculate used leave days by type
  approvedLeaves.forEach(leave => {
    const days = leave.working_days !== undefined && leave.working_days !== null
      ? Number(leave.working_days) 
      : calculateDuration(leave.start_date, leave.end_date)
    
    const validDays = isNaN(days) ? 0 : days
    
    const isSickLeave = sickLeaveTypeIds.includes(Number(leave.leave_type_id)) || 
                        (leave.is_sick_leave === 1 || leave.is_sick_leave === true)
    
    if (isSickLeave) {
      usedSickLeaveDays += validDays
    } else {
      usedRegularLeaveDays += validDays
    }
  })

  console.log("Used days calculated:", {
    usedRegular: usedRegularLeaveDays,
    usedSick: usedSickLeaveDays
  })

  // FIXED: Calculate total allocations based on actual data (remaining + used = original allocation)
  const totalRegularAllocation = remainingRegularLeave + usedRegularLeaveDays
  const totalSickAllocation = remainingSickLeave + usedSickLeaveDays

  // Calculate percentages
  const regularPercentageUsed = totalRegularAllocation > 0 
    ? (usedRegularLeaveDays / totalRegularAllocation) * 100
    : 0
    
  const sickPercentageUsed = totalSickAllocation > 0 
    ? (usedSickLeaveDays / totalSickAllocation) * 100
    : 0

  const result = {
    regular: {
      total: totalRegularAllocation,
      used: usedRegularLeaveDays,
      remaining: remainingRegularLeave,
      percentage: regularPercentageUsed,
    },
    sick: {
      total: totalSickAllocation,
      used: usedSickLeaveDays,
      remaining: remainingSickLeave,
      percentage: sickPercentageUsed,
    }
  }
  
  console.log("Final statistics based on actual allocations:", result)
  
  return result
}, [calculateDuration])

  const fetchDashboardData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) {
      console.log("🔄 Dashboard fetch already in progress, skipping...")
      return
    }
    
    try {
      fetchingRef.current = true
      setLoading(true)
      setError(null)
      console.log("🔄 Fetching dashboard data...")

      // First, get fresh user data to ensure leave_balance is current
      let freshUserData = null
      if (user && user.id) {
        try {
          freshUserData = await fetchLatestUserData()
          
          if (!isInitialized) {
            await refreshUserData()
            setIsInitialized(true)
          }
        } catch (userError) {
          console.error("Error refreshing user data:", userError)
        }
      }

      // Get leave types
      try {
        const timestamp = new Date().getTime()
        const leaveTypesResponse = await leaveService.getLeaveTypes({ _t: timestamp })
        
        if (leaveTypesResponse && leaveTypesResponse.leaveTypes) {
          setLeaveTypes(leaveTypesResponse.leaveTypes)
        } else {
          setLeaveTypes([])
        }
      } catch (error) {
        console.error("Error fetching leave types:", error)
        setLeaveTypes([])
      }

      // Get user's leave requests
      try {
        const timestamp = new Date().getTime()
        const leaveResponse = await leaveService.getUserLeaveRequests(null, { _t: timestamp })

        if (leaveResponse && leaveResponse.leaveRequests) {
          const requests = leaveResponse.leaveRequests
          setLeaveRequests(requests)

          // Calculate leave statistics using the best available user data
          const userDataForStats = freshUserData || directUserData || user
          
          if (userDataForStats) {
            const stats = calculateLeaveStatistics(requests, leaveTypes, userDataForStats)
            
            // Update with calculated values while keeping the remaining value from direct API
            setLeaveStats({
              total: stats.regular.total,
              used: stats.regular.used,
              remaining: userDataForStats.leave_balance || 0,
              percentage: stats.regular.percentage
            })
            
            setSickLeaveStats({
              total: stats.sick.total,
              used: stats.sick.used,
              remaining: userDataForStats.sick_leave_balance || 0,
              percentage: stats.sick.percentage
            })
          }

          // Sort and set upcoming leaves
          if (requests.length > 0) {
            const sortedRequests = [...requests].sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            setUpcomingLeaves(sortedRequests)
          } else {
            setUpcomingLeaves([])
          }
        } else {
          setLeaveRequests([])
          setUpcomingLeaves([])
        }
      } catch (leaveError) {
        console.error("Error fetching user leave requests:", leaveError)
        setLeaveRequests([])
        setUpcomingLeaves([])
      }

      // For managers and admins, get pending leave requests
      if (user?.role === "manager" || user?.role === "admin") {
        try {
          const timestamp = new Date().getTime()
          const pendingResponse = await leaveService.getAllLeaveRequests({ status: "pending", _t: timestamp })
          setPendingApprovals(pendingResponse.leaveRequests || [])
        } catch (pendingError) {
          console.error("Error fetching pending approvals:", pendingError)
          setPendingApprovals([])
        }
      }

      // Get all holidays for the current year - fixed to match expected function signature
      try {
        const today = new Date()
        const currentYear = today.getFullYear()
        const endOfYear = new Date(currentYear, 11, 31)
        
        // Format dates and add timestamp to prevent caching
        const startDateStr = today.toISOString().split('T')[0]
        const endDateStr = endOfYear.toISOString().split('T')[0]
        const timestamp = new Date().getTime()
        
        // Pass parameters exactly as expected by the service
        const holidayResponse = await holidayService.getHolidaysInRange(
          startDateStr,
          endDateStr,
          { _t: timestamp }
        )

        // Sort holidays by date
        const sortedHolidays = holidayResponse.holidays
          ? [...holidayResponse.holidays].sort((a, b) => new Date(a.date) - new Date(b.date))
          : []

        console.log("✅ Fetched holidays:", sortedHolidays)
        setUpcomingHolidays(sortedHolidays)
      } catch (holidayError) {
        console.error("Error fetching holidays:", holidayError)
        setUpcomingHolidays([])
      }

      setLastRefreshed(new Date())
      setLoading(false)
    } catch (err) {
      console.error("❌ Error fetching dashboard data:", err)
      setError("Failed to load dashboard data. Please try again later.")
      setLoading(false)
    } finally {
      fetchingRef.current = false // Always reset the flag
    }
  }, [user, fetchLatestUserData, refreshUserData, isInitialized, calculateLeaveStatistics, leaveTypes, directUserData])

  // Initialize dashboard - FIXED to avoid dependency issues
  const initializeDashboard = useCallback(async () => {
    // Skip if already initialized or no user
    if (dashboardInitiatedRef.current || !user?.id) {
      if (!user?.id) {
        console.log("⏳ Dashboard waiting for user data...")
      }
      return
    }
    
    console.log("🔄 Initializing dashboard once for user:", user.id)
    dashboardInitiatedRef.current = true
    
    // Add a small delay to prevent multiple rapid calls
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // Initial data fetch
    await fetchDashboardData()
  }, [user?.id, fetchDashboardData])

  // Run initialization
  useEffect(() => {
    initializeDashboard()
  }, [initializeDashboard])

  // Set up event listeners (separate effect)
  useEffect(() => {
    const handleLeaveUpdate = (event) => {
      if (!event.detail?.userId || event.detail.userId === user?.id) {
        console.log("📨 Leave update event received, refreshing data")
        fetchDashboardData()
      }
    }
    
    const handleStorageUpdate = () => {
      const wasUpdated = localStorage.getItem("leave_balance_updated")
      if (wasUpdated === "true") {
        console.log("💾 Leave balance was updated elsewhere, refreshing data")
        fetchDashboardData()
        localStorage.removeItem("leave_balance_updated")
      }
    }
    
    // Set up event listeners
    window.addEventListener("leave-balance-update", handleLeaveUpdate)
    window.addEventListener("leave-request-updated", handleLeaveUpdate)
    window.addEventListener("focus", handleStorageUpdate)
    
    // Clean up
    return () => {
      window.removeEventListener("leave-balance-update", handleLeaveUpdate)
      window.removeEventListener("leave-request-updated", handleLeaveUpdate)
      window.removeEventListener("focus", handleStorageUpdate)
    }
  }, [user?.id, fetchDashboardData])

  // Set initial state based on current user - important for proper initialization
  // Set initial state based on current user - FIXED to prevent NaN
// Set initial state based on current user - REMOVED hardcoded defaults
useEffect(() => {
  if (user) {
    const annualBalance = Number(user.leave_balance) || 0
    const sickBalance = Number(user.sick_leave_balance) || 0
    
    console.log("Setting initial balances from user:", {
      annual: annualBalance,
      sick: sickBalance
    })
    
    // Don't set hardcoded totals - let the calculation determine the actual allocation
    setLeaveStats(prev => ({
      ...prev,
      remaining: annualBalance
      // Remove: total: 21 - this should be calculated
    }))
    
    setSickLeaveStats(prev => ({
      ...prev,
      remaining: sickBalance
      // Remove: total: 10 - this should be calculated
    }))
  }
}, [user])

  // Check for refresh state from navigation
  useEffect(() => {
    if (location.state?.refresh) {
      console.log("🔄 Refresh flag detected in location state, refreshing data")
      fetchDashboardData()
      // Clear the refresh flag from history
      window.history.replaceState({}, document.title)
    }
  }, [location.state, fetchDashboardData])

  // Format date to readable string
  const formatDate = useCallback((dateString) => {
    if (!dateString) return ""

    try {
      const options = { year: "numeric", month: "long", day: "numeric" }
      return new Date(dateString).toLocaleDateString(undefined, options)
    } catch (e) {
      console.error("Error formatting date:", e)
      return dateString
    }
  }, [])

  // Handle refresh button click
  const handleRefresh = useCallback(async () => {
    await fetchDashboardData()
  }, [fetchDashboardData])

  // Get status badge color based on leave status
  const getStatusBadgeClass = useCallback((status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800"
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "rejected":
        return "bg-red-100 text-red-800"
      case "cancelled":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-blue-100 text-blue-800"
    }
  }, [])

  // Handle debug reset
  const handleDebugReset = useCallback(() => {
    dashboardInitiatedRef.current = false
    localStorage.setItem("leave_balance_updated", "true")
    console.log("🔄 Reset initialization flags and triggered update")
    fetchDashboardData()
  }, [fetchDashboardData])

  if (loading && !user) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading dashboard...</div>
      </AppLayout>
    )
  }

  if (error) {
    return (
      <AppLayout>
        <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">{error}</div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Inject custom scrollbar styles */}
      <style>{scrollbarStyles}</style>

      <div className="space-y-6">
        {/* Welcome section with Request Leave button */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome, {user?.name || "User"}!</h1>
            <p className="text-gray-600">
              Today is{" "}
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
          <Link to="/leave-requests/new">
            <Button size="lg" className="font-semibold bg-indigo-600 hover:bg-indigo-700">
              Request Leave
            </Button>
          </Link>
        </div>

        {/* Stats section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Leave balance card */}
          <Card className="border-l-4 border-indigo-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-700">Annual Leave</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-6 text-indigo-600"
                onClick={handleRefresh}
                title="Refresh balance data"
              >
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-3 text-indigo-700">{leaveStats.remaining} days</div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full" 
                  style={{ width: `${leaveStats.percentage}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {leaveStats.used} of {leaveStats.total} days used ({leaveStats.percentage.toFixed(0)}%)
              </p>
              <div className="text-xs text-gray-400 mt-2">Last refreshed: {lastRefreshed.toLocaleTimeString()}</div>
            </CardContent>
          </Card>

          {/* Sick Leave balance card */}
          <Card className="border-l-4 border-rose-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-700">Sick Leave</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-6 text-rose-600"
                onClick={handleRefresh}
                title="Refresh sick leave data"
              >
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-3 text-rose-700">{sickLeaveStats.remaining} days</div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-rose-500 h-2.5 rounded-full" style={{ width: `${sickLeaveStats.percentage}%` }}></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {sickLeaveStats.used} of {sickLeaveStats.total} days used ({sickLeaveStats.percentage.toFixed(0)}%)
              </p>
              <div className="text-xs text-gray-400 mt-2">Medical absences & emergencies</div>
            </CardContent>
          </Card>

          {/* Pending requests card */}
          <Card className="border-l-4 border-yellow-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-gray-700">Pending Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-3 text-yellow-600">
                {leaveRequests.filter((req) => req.status === "pending").length}
              </div>
              <p className="text-sm text-gray-500">
                You have {leaveRequests.filter((req) => req.status === "pending").length} pending leave requests
              </p>
              <Link to="/leave-requests">
                <Button variant="outline" className="mt-4 border-yellow-500 text-yellow-700 hover:bg-yellow-50">
                  View All Requests
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Upcoming holiday card */}
          <Card className="border-l-4 border-emerald-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-gray-700">Next Holiday</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingHolidays.length > 0 ? (
                <>
                  <div className="text-3xl font-bold mb-3 text-emerald-600">{upcomingHolidays[0].name}</div>
                  <p className="text-sm text-gray-500">{formatDate(upcomingHolidays[0].date)}</p>
                </>
              ) : (
                <p className="text-gray-500">No upcoming holidays</p>
              )}
              <Link to="/calendar">
                <Button variant="outline" className="mt-4 border-emerald-500 text-emerald-700 hover:bg-emerald-50">
                  View Calendar
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Main content sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pending approvals section (for managers/admins) */}
          {(user?.role === "manager" || user?.role === "admin") && (
            <Card className="border-t-4 border-blue-500 hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-blue-50 rounded-t-lg pb-2">
                <CardTitle className="text-lg font-semibold text-gray-700">Pending Approvals</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {pendingApprovals.length > 0 ? (
                  <div className="space-y-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                    {pendingApprovals.slice(0, 5).map((request) => (
                      <div
                        key={request.id}
                        className="flex justify-between items-center pb-3 border-b hover:bg-blue-50 p-2 rounded transition-colors"
                      >
                        <div>
                          <p className="font-medium text-blue-800">{request.user_name}</p>
                          <p className="text-sm text-gray-600">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)}
                          </p>
                          <div className="flex items-center mt-1">
                            <p className="text-sm">{request.leave_type}</p>
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-lg">
                              {request.working_days || calculateDuration(request.start_date, request.end_date)} days
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Link to={`/leave-requests/review/${request.id}`}>
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                              Review
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                    {pendingApprovals.length > 5 && (
                      <div className="text-center mt-4">
                        <Link to="/leave-requests">
                          <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                            View All ({pendingApprovals.length})
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">No pending leave requests to approve</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Your leave section - shows ALL leave requests */}
          <Card className="border-t-4 border-green-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between bg-green-50 rounded-t-lg pb-2">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-700">Your Leave Requests</CardTitle>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm font-medium">
                    {leaveStats.remaining} annual days left
                  </span>
                  <span className="inline-block px-2 py-1 bg-rose-100 text-rose-700 rounded-md text-sm font-medium">
                    {sickLeaveStats.remaining} sick days left
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                className="text-green-700 hover:bg-green-100"
                title="Refresh leave data"
              >
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="pt-4">
              {Array.isArray(upcomingLeaves) && upcomingLeaves.length > 0 ? (
                <div className="space-y-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                  {upcomingLeaves.map((leave) => {
                    // Use the working_days value from the API or calculate duration between dates
                    const leaveDays = leave.working_days !== undefined && leave.working_days !== null
                      ? Number(leave.working_days) 
                      : calculateDuration(leave.start_date, leave.end_date)
                    
                    const leaveType = leave.leave_type_name || leave.leave_type || "Annual Leave"
                    const statusClass = getStatusBadgeClass(leave.status)
                    
                    // Check if it's sick leave - check both leave type and is_sick_leave flag
                    const isSickLeave = (Array.isArray(leaveTypes) && leaveTypes.some(type => 
                      type.name && type.name.toLowerCase().includes('sick') && 
                      type.id === Number(leave.leave_type_id)
                    )) || leave.is_sick_leave === 1 || leave.is_sick_leave === true
                    
                    // Add style for sick leave
                    const leaveTypeClass = isSickLeave ? "text-rose-800" : "text-green-800"
                    const daysBadgeClass = isSickLeave ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"

                    return (
                      <div
                        key={leave.id || `leave-${Math.random()}`}
                        className="pb-3 border-b hover:bg-green-50 p-2 rounded transition-colors"
                      >
                        <div className="flex justify-between">
                          <div className="flex items-center">
                            <p className={`font-medium ${leaveTypeClass}`}>{leaveType}</p>
                            <span className={`ml-2 text-xs ${daysBadgeClass} px-2 py-0.5 rounded-lg`}>
                              {leaveDays} days
                            </span>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${statusClass}`}>
                            {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-sm text-gray-600">
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </p>
                        </div>
                        {leave.reason && (
                          <p className="text-xs text-gray-500 mt-2 truncate">
                            <span className="font-medium">Reason:</span> {leave.reason}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-gray-500">No leave requests found</p>
              )}
              <div className="text-center mt-4">
                <Link to="/leave-requests">
                  <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                    View All Leave Requests
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming holidays section - Now shows all remaining holidays for the year */}
          <Card className="md:col-span-2 border-t-4 border-amber-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="bg-amber-50 rounded-t-lg pb-2 flex justify-between items-center">
              <CardTitle className="text-lg font-semibold text-gray-700">
                Remaining Holidays for {new Date().getFullYear()}
              </CardTitle>
              <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                {upcomingHolidays.length} holidays left
              </span>
            </CardHeader>
            <CardContent className="pt-4">
              {upcomingHolidays.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {upcomingHolidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="p-4 border rounded-lg hover:bg-amber-50 transition-colors shadow-sm"
                    >
                      <p className="font-medium text-amber-800">{holiday.name}</p>
                      <p className="text-sm text-gray-600 mt-1">{formatDate(holiday.date)}</p>
                      {holiday.is_recurring && (
                        <span className="mt-2 inline-block px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                          Annual
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No more holidays left for this year</p>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Debug section with all state info - helpful for troubleshooting */}
        <details className="mt-2 text-xs text-gray-400 p-4 border border-gray-200 rounded-md">
          <summary>Debug Info</summary>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium">Annual Leave</h4>
              <p>Context leave_balance: {user?.leave_balance}</p>
              <p>Direct API leave_balance: {directUserData?.leave_balance}</p>
              <p>State leaveStats.remaining: {leaveStats.remaining}</p>
              <p>Used days: {leaveStats.used}</p>
              <p>Total allocation: {leaveStats.total}</p>
              <p>Utilization: {leaveStats.percentage.toFixed(2)}%</p>
            </div>
            <div>
              <h4 className="font-medium">Sick Leave</h4>
              <p>Context sick_leave_balance: {user?.sick_leave_balance}</p>
              <p>Direct API sick_leave_balance: {directUserData?.sick_leave_balance}</p>
              <p>State sickLeaveStats.remaining: {sickLeaveStats.remaining}</p>
              <p>Used days: {sickLeaveStats.used}</p>
              <p>Total allocation: {sickLeaveStats.total}</p>
              <p>Utilization: {sickLeaveStats.percentage.toFixed(2)}%</p>
            </div>
          </div>
          <div className="mt-2">
            <h4 className="font-medium">Component State</h4>
            <p>Loading: {loading ? 'true' : 'false'}</p>
            <p>Initialized: {isInitialized ? 'true' : 'false'}</p>
            <p>Dashboard Initiated: {dashboardInitiatedRef.current ? 'true' : 'false'}</p>
            <p>Last Refreshed: {lastRefreshed.toLocaleTimeString()}</p>
            <p>Upcoming Holidays: {upcomingHolidays.length}</p>
            <p>Leave Requests Count: {leaveRequests.length}</p>
            <p>Upcoming Leaves Count: {upcomingLeaves.length}</p>
          </div>
          <div className="mt-2">
            <h4 className="font-medium">Leave Requests</h4>
            {leaveRequests.length > 0 ? (
              <div className="mt-1 max-h-40 overflow-auto">
                {leaveRequests.map(req => (
                  <div key={req.id} className="text-xs mb-1">
                    ID: {req.id} - Type: {req.leave_type} - Status: {req.status} - 
                    Days: {req.working_days || calculateDuration(req.start_date, req.end_date)}
                  </div>
                ))}
              </div>
            ) : (
              <p>No leave requests</p>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button 
              onClick={handleRefresh} 
              className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs"
            >
              Force Refresh
            </button>
            <button 
              onClick={handleDebugReset} 
              className="px-2 py-1 bg-blue-200 text-blue-600 rounded text-xs"
            >
              Reset & Refresh
            </button>
          </div>
        </details>
      </div>
    </AppLayout>
  )
}

export default DashboardPage